/* eslint-disable react/jsx-no-undef */
import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  IconButton,
  Box,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import { showWarning } from "../../../../api"

const ModalLotesIngreso = ({ open, onClose, onSave, articulo, bodcodigo }) => {
  const [rows, setRows] = useState([])
  const cantidadAAfectar = parseFloat(articulo?.cant_afectar) || 0

  // ==========================================================================
  // INICIALIZACIÓN DINÁMICA DE FILAS
  // ==========================================================================
  useEffect(() => {
    if (open && articulo) {
      // Si ya existían lotes capturados previamente, los restauramos
      if (articulo.lotes && articulo.lotes.length > 0) {
        setRows([...articulo.lotes])
      } else {
        // Inicializamos con una fila por defecto
        const hoy = new Date().toISOString().split("T")[0] // Formato YYYY-MM-DD
        setRows([
          {
            lotenumero: "",
            cantidad: cantidadAAfectar > 0 ? cantidadAAfectar : "", // Sugerimos la cantidad total inicial
            elaboracion: hoy,
            vencimiento: hoy,
          },
        ])
      }
    }
  }, [open, articulo, cantidadAAfectar])

  // ==========================================================================
  // MANEJO DE FILAS Y DATOS
  // ==========================================================================
  const handleInputChange = (index, field, value) => {
    setRows((prevRows) => {
      const newRows = [...prevRows]
      // El número de lote se fuerza a mayúsculas para la base de datos
      newRows[index][field] = field === "lotenumero" ? value.toUpperCase() : value
      return newRows
    })
  }

  const handleAddRow = () => {
    const hoy = new Date().toISOString().split("T")[0]
    setRows([
      ...rows,
      {
        lotenumero: "",
        cantidad: "",
        elaboracion: hoy,
        vencimiento: hoy,
      },
    ])
  }

  const handleRemoveRow = (index) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index))
  }

  // ==========================================================================
  // CÁLCULOS Y VALIDACIÓN ESTRICTA
  // ==========================================================================
  const totalDistribuido = rows.reduce((acc, row) => acc + (parseFloat(row.cantidad) || 0), 0)
  const isCuadrado = totalDistribuido === cantidadAAfectar

  const handleSave = () => {
    // 1. Validar que existan filas
    if (rows.length === 0) {
      return showWarning("Debe ingresar al menos un lote.")
    }

    // 2. Cuadre exacto de cantidades
    if (!isCuadrado) {
      return showWarning(
        `La suma de las cantidades de los lotes (${totalDistribuido}) no cuadra con la cantidad a ingresar (${cantidadAAfectar}).`,
      )
    }

    // 3. Validación de campos internos y duplicados en memoria
    const lotesSet = new Set()

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]

      if (!row.lotenumero.trim()) {
        return showWarning(`El número de lote en la fila ${i + 1} está en blanco.`)
      }

      if (lotesSet.has(row.lotenumero)) {
        return showWarning(
          `El lote "${row.lotenumero}" está duplicado en la fila ${i + 1}. Agrúpelo en una sola línea si se trata del mismo lote.`,
        )
      }
      lotesSet.add(row.lotenumero)

      if (!row.cantidad || parseFloat(row.cantidad) <= 0) {
        return showWarning(`La cantidad en el lote "${row.lotenumero}" debe ser mayor a cero.`)
      }

      if (!row.elaboracion) {
        return showWarning(`Falta la fecha de elaboración en el lote "${row.lotenumero}".`)
      }

      if (!row.vencimiento) {
        return showWarning(`Falta la fecha de vencimiento en el lote "${row.lotenumero}".`)
      }

      // Validación lógica de fechas
      if (new Date(row.vencimiento) < new Date(row.elaboracion)) {
        return showWarning(
          `La fecha de vencimiento no puede ser menor a la elaboración en el lote "${row.lotenumero}".`,
        )
      }
    }

    // Si todo es correcto, enviamos el arreglo al componente padre
    onSave(rows)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#196C87", color: "white", py: 1.5 }}>Ingreso de Nuevos Lotes</DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Artículo: <b>{articulo?.artcodigo}</b> - {articulo?.artdescri}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Bodega: {bodcodigo}
          </Typography>
          <Typography variant="body2" color={isCuadrado ? "primary" : "error"} fontWeight="bold">
            Total Distribuido: {totalDistribuido} / {cantidadAAfectar}
          </Typography>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="30%">Número de Lote *</TableCell>
                <TableCell width="20%">Cantidad *</TableCell>
                <TableCell width="20%">F. Elaboración *</TableCell>
                <TableCell width="20%">F. Vencimiento *</TableCell>
                <TableCell width="10%" align="center">
                  <Tooltip title="Agregar Lote">
                    <IconButton color="primary" onClick={handleAddRow} size="small">
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={row.lotenumero}
                      onChange={(e) => handleInputChange(index, "lotenumero", e.target.value)}
                      inputProps={{ maxLength: 50 }}
                      placeholder="Ej: L-001"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                      value={row.cantidad}
                      onChange={(e) => handleInputChange(index, "cantidad", e.target.value)}
                      inputProps={{ min: 0, step: "0.01" }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="date"
                      value={row.elaboracion}
                      onChange={(e) => handleInputChange(index, "elaboracion", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="date"
                      value={row.vencimiento}
                      onChange={(e) => handleInputChange(index, "vencimiento", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveRow(index)}
                      size="small"
                      disabled={rows.length === 1} // Evita borrar si solo hay 1 fila
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: "#f5f7fa" }}>
        <Button onClick={onClose} color="inherit" variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!isCuadrado}
          sx={{ backgroundColor: "#196C87" }}
        >
          Confirmar Lotes
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalLotesIngreso
